/* eslint-disable no-console */
/* eslint-disable no-constant-condition */
import { useChatMessagesContext } from '@/Context/Chat/ChatMessagesContext';
import useCopyHtmlFormat from '@/Hooks/useCopyHtmlFormat';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { UserCircleIcon } from '@heroicons/react/20/solid';
import * as outlineIcons from '@heroicons/react/24/outline';
import {
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  Bars3Icon,
  ClipboardDocumentIcon,
  ExclamationCircleIcon,
  MinusCircleIcon,
  PaintBrushIcon,
  PlusCircleIcon,
  PlusIcon,
  SparklesIcon,
  SpeakerWaveIcon,
  StarIcon as StarIconOutline,
  XCircleIcon as XCircleIconOutline,
} from '@heroicons/react/24/outline';
import {
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  PaintBrushIcon as PaintBrushIconSolid,
  StarIcon as StarIconSolid,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { Box, CssBaseline, IconButton, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import useChatStore, { COLUMNS, GRID, ROWS, TABS } from 'Components/Chat/store';
import useChatMessageStore from 'Components/Common/Markdown/store';
import TextAreaEditMessage from 'Components/Common/Markdown/TextAreaEditMessage';

import {
  CustomSuccessToast,
  CustomToast,
} from '@/@straico-ui/commons/CustomToasts';
import { highlightWordsTextAreaV2, IMAGES_TEMPLATES_IDS } from 'Constants/chat';
import DraggableTab from 'Features/SmartMerge/DraggableTab';
import DroppableZone from 'Features/SmartMerge/DroppableZone';
import useSmartMergeStore, {
  MERGE_STATES,
  MESSAGE_ACTIONS,
} from 'Features/SmartMerge/store';
import { SelectionChangeWarning } from 'Features/Core/Components/SmartMerge/SmartMergeDialog';
import { ConversationPathWarning } from 'Features/Core/Components/TreeView/TreeView';
import { AnimatePresence, motion } from 'framer-motion';
import useClipboardComponent from 'Hooks/useClipboardComponent';
import useIsMobile from 'Hooks/useIsMobile';
import { get } from 'lodash';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import StraiCoinIcon from 'Theme/icons/straicoin';
import useThemeStore from 'Theme/store';
import Skeleton from 'utils/Skeleton';
import PencilSquareIcon from '../../../Theme/icons/editIcon';
import ModelsIcon from '../../../Theme/icons/modelsIcon';
import { icons as modelIcons } from '../../../Theme/icons/modelsIcons/index';
import SmartMergeIcon from '../../../Theme/icons/SmartMergeIcon';
import SmartMergeDoubleRing from '@/Theme/icons/smartMergeDoubleRing';
import TreeLogo from 'Theme/icons/sideBySideIcons/tree';
import useTreeDNDChatStore from '../../Chat/TreeDNDChat/Store/index.js';
import ContentMessage from './ContentMessage';
import DeleteMessage from './DeleteMessage';
import DownloadOptionsDialog, {
  hasJSONStructure,
  hasTableStructure,
} from './DownloadFromMessage';
import PromptOptions from './PromptOptions';
import useCountCoinsEdit from './useCountCoinsEdit';
import {
  getInitFavorite,
  getInitMerge,
  getInitModel,
  truncateMessage,
} from './utils';
import { cn } from 'utils/styles';
import ModelProviderError from 'Features/Core/Components/StreamingMessage/components/ModelProviderError';

const ChatMessage = inject('store')(
  observer(
    ({
      store,
      item,
      index,
      avatar,
      onDelete,
      onDeleteResponse,
      onCapturedScript,
      onLoading,
      onOpenFull,
      changeOutput,
      chatId,
      onRegenerate,
      onRegenerateEdit,
      continueResponse,
      loading,
      optimizing,
      calculateCoinsEdit,
      onAddModels,
      readOnlyMessage = false, // For favorite messages
      onSend,
      onScrollRequest,
      // Tree props
      subsequentResponseCount,
      activeSubsequentDotIndices,
      onDotClick,
      messageIndexForDots,
      registerDotRef,
      registerCardRef,
      selectedSubsequentResponse,
      onCardReorder,
      subsequentResponsesHaveSpecificCapabilities,
      // Ring click props
      onRingClickFromUser, // For user messages to call when ring is clicked
      ringClickSignal, // For AI responses to listen for ring click signals
      userMessageIndex, // For AI responses to know which user message they belong to
      isLatestUserMessage, // Only show rings on the most recent user message
      isLatestAIResponseBlock, // For AI responses to know if they belong to the latest user message
      // Dot reorder props
      dotReorderSignal, // For user messages to listen for dot reorder signals
    }) => {
      const chatContainerRef = useRef(null);

      const isMobile = useIsMobile();
      const { copy } = useClipboardComponent();
      const { theme: themeValue } = useThemeStore();
      const { capabilities } = useChatMessagesContext();
      const { handleCopyHtmlFormat } = useCopyHtmlFormat({
        chatContainerRef,
      });

      const [localModels, setLocalModels] = useState(() => {
        const existing = localStorage.getItem('currentModels');
        return existing ? JSON.parse(existing) : [];
      });

      useEffect(() => {
        async function fetchModelsIfEmpty() {
          if (!localModels.length) {
            try {
              const response = await store.api.get(`/model`);
              if (response.data?.models) {
                localStorage.setItem(
                  'currentModels',
                  JSON.stringify(response.data.models)
                );
                setLocalModels(response.data.models);
              }
            } catch (err) {
              toast.error('Failed to load models');
            }
          }
        }

        fetchModelsIfEmpty();
      }, [localModels.length, store.api]);

      const [editValue, setEditValue] = useState('');
      const [hoveredIndex, setHoveredIndex] = useState(null);
      const [imagesPreview, setImagesPreview] = useState([]);
      const [editSelected, setEditSelected] = useState(false);
      const [selectedModels, setSelectedModels] = useState([]);
      const readonlyTools = Object.values(IMAGES_TEMPLATES_IDS);
      const [modelSelected, setModelSelected] = useState(getInitModel(item));

      const [deleteTitle, setDeleteTitle] = useState(null);
      const [deleteMessage, setDeleteMessage] = useState(null);
      const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
      const [availableDownloadTypes, setAvailableDownloadTypes] = useState([]);
      const [messageToDownload, setMessageToDownload] = useState(null);
      const [deleteResponse, setDeleteResponse] = useState(false);
      const [compatibleModels, setCompatibleModels] = useState([]);

      const [expandedIdx, setExpandedIdx] = useState([]);
      const [changeSelection, setChangeSelection] = useState(false);
      const [editModelSelected, setEditModelSelected] = useState({});
      const [currentEditMessages, setCurrentEditMessages] = useState([]);
      const [previousAiIndexResponse, setPreviousAiIndexResponse] = useState(0);

      const chat = useChatStore((state) => state.selectedChat).chat;
      const modelsList = useChatStore((state) => state.modelsList);
      const setModelsList = useChatStore((state) => state.setModelsList);
      const renderType = useChatStore((state) => state.renderType);
      const powerPanelOpen = useChatStore((state) => state.powerPanelOpen);
      const isModelProviderError = useChatStore(
        (state) => state.isModelProviderError
      );

      const [activeMerge, setActiveMerge] = useState(getInitMerge(item));
      const [newHeight, setNewHeight] = useState(null);
      const cardRefs = useRef({});

      const mergeActive = useSmartMergeStore((state) => state.active);
      const setActive = useSmartMergeStore((state) => state.setActive);
      const messages = useSmartMergeStore((state) => state.messages);
      const updateMessages = useSmartMergeStore(
        (state) => state.updateMessages
      );
      const setMinimized = useSmartMergeStore((state) => state.setMinimized);
      const isMinimized = useSmartMergeStore((state) => state.isMinimized);
      const mergeState = useSmartMergeStore((state) => state.activeTab);
      const mergeItem = useSmartMergeStore((state) => state.item);
      const setMergeItem = useSmartMergeStore((state) => state.setItem);
      const setActiveTab = useSmartMergeStore((state) => state.setActiveTab);
      const messageRefs = useRef({});

      const addToRefs = (el, index) => {
        if (el) {
          cardRefs.current[index] = el;
        }
      };

      const [isEditing, setIsEditing] = useState(false);

      // Lazy state for custom dot order (only created when reordering happens)
      const [customDotOrder, setCustomDotOrder] = useState(null);

      const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
      const [initialPosition, setInitialPosition] = useState(null);
      const [longPressTimeout, setLongPressTimeout] = useState(null);
      const [isDragging, setIsDragging] = useState(false);
      const MOVEMENT_THRESHOLD = 5;

      useEffect(() => {
        if (modelsList.length === 0) {
          getModels();
        }
      }, []);

      useEffect(() => {
        const handleCopy = (e) => {
          const selection = window.getSelection();
          if (isEditing) {
            e.preventDefault();
            return;
          }
          handleCopyHtmlFormat(e, selection);
        };

        document.addEventListener('copy', handleCopy);

        return () => {
          document.removeEventListener('copy', handleCopy);
        };
      }, [isEditing, chatContainerRef]);

      // Check for Download options
      useEffect(() => {
        const checkDownloadTypes = (content) => {
          const types = [];
          if (hasTableStructure(content)) types.push('csv');
          if (hasJSONStructure(content)) types.push('json');
          return types;
        };
        // Handle multiple model responses
        if (Array.isArray(item)) {
          const allTypes = item.reduce((types, modelResponse) => {
            if (modelResponse?.data?.content) {
              types.push(...checkDownloadTypes(modelResponse?.data?.content));
            }
            return types;
          }, []);
          setAvailableDownloadTypes([...new Set(allTypes)]);
        } else if (item?.data?.content) {
          // Handle single response
          setAvailableDownloadTypes(checkDownloadTypes(item?.data?.content));
        }
      }, [item]);

      // Hook to count words
      const { words } = useCountCoinsEdit({
        text: editValue,
        messages: currentEditMessages,
        editModelSelected: editModelSelected,
        editSelected: editSelected,
      });

      const { editMessChange } = useChatMessageStore();

      const [showDeleteMessageDialog, setShowDeleteMessageDialog] =
        useState(false);

      const [messageToDelete, setMessageToDelete] = useState(null);

      const [deleted, setDeleted] = useState(false);

      const [favorite, setFavorite] = useState(false);

      // Selection change warning modal state
      const [selectionChangeWarning, setSelectionChangeWarning] = useState({
        open: false,
        pendingSelection: null,
      });

      // Conversation path warning modal state
      const [conversationPathWarning, setConversationPathWarning] = useState({
        open: false,
        pendingSelection: null,
      });

      const handleCloseSelectionWarning = () => {
        setSelectionChangeWarning({
          open: false,
          pendingSelection: null,
        });
      };

      const handleConfirmSelectionWarning = async () => {
        const { element, index } = selectionChangeWarning.pendingSelection;

        // Close SmartMerge first
        setActive(false);

        // Then apply the selection change
        if (!Array.isArray(item)) {
          handleCloseSelectionWarning();
          return;
        }
        let realIndex = index;
        if (item.every((i) => i._id)) {
          realIndex = item.findIndex((i) => i._id === element._id);
        }
        setModelSelected(element.modelRef);
        setFavorite(element?.is_favorite ?? false);
        if (readOnlyMessage) {
          await selectedOutputReadOnly(element);
        } else {
          changeOutput(element);
        }

        handleCloseSelectionWarning();
      };

      const handleCloseConversationPathWarning = () => {
        setConversationPathWarning({
          open: false,
          pendingSelection: null,
        });
      };

      const handleConfirmConversationPathWarning = async () => {
        const { element, index } = conversationPathWarning.pendingSelection;

        // Apply the selection change (same logic as normal handleOutput)
        if (!Array.isArray(item)) {
          handleCloseConversationPathWarning();
          return;
        }
        let realIndex = index;
        if (item.every((i) => i._id)) {
          realIndex = item.findIndex((i) => i._id === element._id);
        }
        setModelSelected(element.modelRef);
        setFavorite(element?.is_favorite ?? false);
        if (readOnlyMessage) {
          await selectedOutputReadOnly(element);
        } else {
          changeOutput(element);
        }

        handleCloseConversationPathWarning();
      };

      const handleCopy = (item) => {
        try {
          let rawText = Array.isArray(item?.data?.content)
            ? item?.data?.content
                .filter((obj) => obj.type === 'text')
                .map((obj) => obj.text)
                .join('\n')
            : item?.data?.content;
          copy(rawText);
          toast.custom(
            (t) => (
              <CustomToast
                id={t.id}
                message="To copy in HTML format, highlight the text you want to copy and then use your standard copy function."
              />
            ),
            { duration: 5000 }
          );
          toast.custom(
            (t) => (
              <CustomSuccessToast message="Copied to clipboard in markdown forma" />
            ),
            { duration: 1500 }
          );
        } catch (error) {
          toast.error('Error copying to clipboard');
        }
      };

      const addReferenceFile = async (e) => {
        onLoading(true);
        try {
          toast.loading(
            "Adding your image to attachments - find it under the '+' icon once it's ready",
            {
              id: 'loading-save-file',
            }
          );
          await store.api.post(`/file`, e);
          toast.dismiss('loading-save-file');
          toast.success('File added to your attachments');
          onLoading(false);
        } catch (error) {
          if (error?.response?.data?.error?.message) {
            toast.error(error?.response?.data?.error?.message);
          }
          onLoading(false);
        }
      };

      const handleMessageForTTS = (value) => {
        if (onCapturedScript) {
          let rawText = Array.isArray(value.data.content)
            ? value.data.content
                .filter((obj) => obj.type === 'text')
                .map((obj) => obj.text)
                .join('\n')
            : value.data.content;

          onCapturedScript(rawText);
        }
      };
      const handleDelete = (messageId, isResponse = false) => {
        if (isResponse) {
          setDeleteTitle('Delete response');

          setDeleteMessage('Are you sure that you wish to delete this answer?');
          setDeleteResponse(true);
        }
        // Implement the logic to delete the message
        setMessageToDelete(messageId);
        setShowDeleteMessageDialog(true);
      };

      const conversationId = chatId || null;

      const handleConfirmDelete = async () => {
        if (deleteResponse) {
          await handleDeleteResponse();
        } else {
          await handleDeleteMessage();
        }
      };

      const handleDeleteMessage = async () => {
        try {
          const response = await store.api.delete(
            `/chat/${chatId}/${messageToDelete}`
          );
          if (response.data.success) {
            // Removing the message from the state
            onDelete(messageToDelete);
            setShowDeleteMessageDialog(false);

            toast.success('Message has been deleted.');
          } else {
            console.error(
              'Failed to delete the message:',
              response.data.message
            );
            toast.error('Failed to delete the message.');
          }
        } catch (error) {
          console.error('Error delete message:', error);
          toast.error('Error deleting message.');
        } finally {
          setDeleteTitle(null);
          setDeleteMessage(null);
          setDeleteResponse(false);
        }
      };

      const handleDeleteResponse = async () => {
        try {
          const response = await store.api.delete(
            `/chat/response/${chatId}/${messageToDelete}`
          );
          if (response.data.success) {
            // Removing the response from the state
            onDeleteResponse(messageToDelete);
            setShowDeleteMessageDialog(false);

            toast.success('Response has been deleted.');
          } else {
            console.error(
              'Failed to delete the response:',
              response.data.message
            );
            toast.error('Failed to delete the response.');
          }
        } catch (error) {
          console.error('Error delete response:', error);
          toast.error('Error deleting response.');
        } finally {
          setDeleteTitle(null);
          setDeleteMessage(null);
          setDeleteResponse(false);
        }
      };

      const handleChange = (e) => {
        setEditValue(e);
      };

      const isReadOnly = (item) => {
        return readonlyTools.includes(item?.tool);
      };

      const handleEditPrompt = (value) => {
        if (loading)
          return toast('Wait a moment, the chat is being processed', {
            icon: '📢',
          });

        let prompt = '';
        if (Array.isArray(value.data.content)) {
          prompt = value.data.content
            .filter((obj) => obj.type === 'text')
            .map((obj) => obj.text)
            .join('\n');
        } else {
          prompt = value.data.content;
        }

        let images = [];
        if (Array.isArray(value.data.content)) {
          images = value.data.content.filter((obj) => obj.type === 'image_url');
        }

        let currentMessages = [...chat.messages];

        let selectedMessageIndex = currentMessages.findIndex(
          (message) => message.date === value.date
        );

        let aiIndexResponse = selectedMessageIndex - 1;

        currentMessages = currentMessages.slice(aiIndexResponse + 2);

        setPrevSelected(selectedMessageIndex + 1);
        setPreviousAiIndexResponse(selectedMessageIndex + 1);
        setCurrentEditMessages(currentMessages);
        setImagesPreview(images);
        setEditValue(prompt);
        setIsEditing(true);
        setEditSelected(true);
      };

      const setPrevSelected = (prevAiIndResp) => {
        let refId;
        let lastMsg = chat.messages[prevAiIndResp];
        if (lastMsg) {
          let filterd = Array.isArray(lastMsg)
            ? lastMsg.filter((m) => m.selected === true)
            : null;
          refId =
            filterd && filterd.length
              ? filterd[0].modelRef
                ? filterd[0].modelRef
                : null
              : lastMsg.modelRef
              ? lastMsg.modelRef
              : null;
        }

        let models = JSON.parse(localStorage.getItem('currentModels'));
        setSelectedModels(models);

        let filteredModels =
          refId && models
            ? models.filter((model) => model._id === refId)
            : null;
        let filterModel =
          filteredModels && filteredModels.length
            ? filteredModels[0]
            : models && models.length
            ? models[0]
            : null;
        setEditModelSelected(filterModel);
        return filterModel;
      };

      useEffect(() => {
        setPrevSelected(previousAiIndexResponse);
      }, [words, editMessChange]);

      const handleMouseEnter = (index) => {
        setHoveredIndex(index);
      };

      const handleMouseLeave = () => {
        setHoveredIndex(null);
      };

      const removeImage = (img) => {
        let modified = imagesPreview.filter(
          (item) => JSON.stringify(item) !== JSON.stringify(img)
        );
        setImagesPreview([...modified]);
      };

      const handleSend = (value) => {
        if (!editValue || editValue.trim() === '') {
          toast("Don't forget: Messages need text too!", {
            icon: '😱',
          });
          return;
        }

        if (!selectedModels.some((model) => model.type === 'vision')) {
          if (imagesPreview.length > 0) {
            return toast(
              "Can't edit messages with images in this Model (LLM)",
              {
                icon: '📢',
              }
            );
          }
        }

        const chatCopy = JSON.parse(JSON.stringify(chat));

        setEditSelected(false);
        setIsEditing(false);
        onRegenerateEdit(
          editValue,
          value.date,
          chat,
          imagesPreview,
          words,
          chatCopy
        );
      };

      const handleCancel = () => {
        setEditSelected(false);
        setIsEditing(false);
      };

      const handleToggleFavorite = async (element, marked) => {
        try {
          const idChat = chat._id ? chat._id : chatId;

          const response = await store.api.put(
            `/chat/${idChat}/message/toggle-mark`,
            {
              isUser: isUser(),
              messageId: isUser() ? null : element._id, // _id for AI messages
              messageDate: isUser() ? element.date : null, // date for user messages
              marked: marked,
            }
          );

          if (response.data.success) {
            // If the message is an array, mark all unique elements
            if (Array.isArray(item)) {
              for (let unique of item) {
                unique.is_favorite = marked;
              }
            } else {
              // If the message is a single element, we just mark it
              element.is_favorite = marked;
            }
            setFavorite(marked);

            if (marked) {
              toast.success(
                'Message starred! This message is now in your Favorite Messages section'
              );
            } else {
              toast.success('Message removed from favorites');
            }
          } else {
            if (marked) {
              toast.error('Failed adding message to favorites');
            } else {
              toast.error('Failed removing message from favorites');
            }
          }
        } catch (error) {
          if (marked) {
            toast.error('Error adding message to favorites');
          } else {
            toast.error('Error removing message from favorites');
          }
        }
      };

      // Set the favorite state when loading is done
      useEffect(() => {
        if (!loading) {
          setFavorite(getInitFavorite(item));
        }
      }, [loading]);

      const isUser = () => {
        return !Array.isArray(item) && item?.data?.role === 'user';
      };

      const getTabs = (item) => {
        return Array.isArray(item)
          ? item.map((ele) => ({
              ...ele,
              isWriting:
                ele?.data?.role !== 'user' &&
                !ele?.finished &&
                ele?.contentLength > 2,
            }))
          : [
              {
                ...item,
                isWriting:
                  item?.data?.role !== 'user' &&
                  !item?.finished &&
                  item?.contentLength > 2,
              },
            ];
      };

      const selectedOutputReadOnly = async (element) => {
        const hash = element._id;
        if (hash) {
          await store.api.post(`/chat/message/${hash}`, {
            chat: chatId,
          });
        }
      };

      const handleOutput = async (element, index) => {
        // Prevent selection changes during active merge operations
        if (mergeActive) {
          setSelectionChangeWarning({
            open: true,
            pendingSelection: { element, index },
          });
          return;
        }

        // Check if this is not the most recent interaction
        const isLatestInteraction = Array.isArray(item)
          ? isLatestAIResponseBlock
          : isLatestUserMessage;

        if (renderType === COLUMNS && isLatestInteraction === false) {
          setConversationPathWarning({
            open: true,
            pendingSelection: { element, index },
          });
          return;
        }

        if (!Array.isArray(item)) {
          return;
        }
        let realIndex = index;
        if (item.every((i) => i._id)) {
          realIndex = item.findIndex((i) => i._id === element._id);
        }
        setModelSelected(element.modelRef);
        setFavorite(element?.is_favorite ?? false);
        if (readOnlyMessage) {
          await selectedOutputReadOnly(element);
        } else {
          changeOutput(element);
        }
      };

      // Determine if element is selected
      const isSelected = (ele) => {
        if (
          !Array.isArray(item) ||
          (ele._id && ele.selected === true && !loading) ||
          (!ele._id && ele.selected === true && loading)
        ) {
          return true;
        }

        let model = modelSelected ? modelSelected : getInitModel(item);
        return model === ele.modelRef;
      };

      // Determine if element is being merged (part of merge operation)
      const isMerging = (ele) => {
        return mergeActive && ele._id && messages.includes(ele._id);
      };

      // Determine if element is a source for the currently selected merge
      const isSourceForSelectedMerge = (ele) => {
        if (!Array.isArray(item)) return false;
        const selectedItem = item.find((i) => i.selected === true);
        if (!selectedItem || !selectedItem.merged) return false;
        return (
          Array.isArray(selectedItem.model) &&
          selectedItem.model.includes(ele.model) &&
          !ele.merged
        );
      };

      const isSelectedNoMerging = (ele) => {
        return (
          isSelected(ele) &&
          (!mergeActive || (mergeActive && item != mergeItem))
        );
      };

      const { search } = useTreeDNDChatStore();

      const isExpanded = (idx) => {
        if (renderType === TABS || isUser()) {
          return true;
        }

        return (
          (window.innerWidth < 768 && expandedIdx.includes(idx)) ||
          window.innerWidth >= 768
        );
      };

      const [items, setItems] = useState(getTabs(item));
      const [dragOverlay, setDragOverlay] = useState(null);
      const [collapsed, setCollapsed] = useState(false);
      const [activeId, setActiveId] = useState(null);

      const [prevTab, setPrevTab] = useState(0);
      const [highlightModel, setHighlightModel] = useState(null);
      const [highlightMerge, setHighlightMerge] = useState(null);

      const [itemOrder, setItemOrder] = useState([]);

      const ChatBubbleContent = (
        item,
        deleted,
        idx,
        topButtons = false,
        tabButtons = null
      ) => {
        let hasSearchTerm = false;
        // THIS VALIDATION IS FOR THE CASE WHEN THE MESSAGE IS AN ARRAY (IMAGES RESPONSES)
        if (typeof item?.data?.content === 'string') {
          hasSearchTerm =
            item?.data?.role !== 'user' &&
            search &&
            item?.data?.content?.toLowerCase().includes(search?.toLowerCase());
        } else if (Array.isArray(item?.data?.content)) {
          hasSearchTerm =
            item?.data?.role !== 'user' &&
            search &&
            item?.data?.content.some((content) => {
              if (content.type === 'text') {
                return content.text
                  .toLowerCase()
                  .includes(search.toLowerCase());
              }
              return false;
            });
        }

        const handleCurrentsHighlights = () => {
          if (!highlightModel || !highlightMerge) return [];

          // Check modelRef
          if (item.modelRef != highlightModel) return [];

          // Get item highlights
          const highlights = item.highlights ? item.highlights : [];
          if (highlights.length == 0) return [];

          const highlight = highlights.find(
            (h) => h.highlightRef == highlightMerge
          );
          return highlight ? highlight.fragments : [];
        };

        return (
          <>
            {!readonlyTools.includes(item?.tool) &&
              getCapabilityState(item?.data?.function, item)}
            <div className={`flex w-full ${hasSearchTerm ? 'highlight' : ''}`}>
              {!editSelected ? (
                <div
                  id={item._id}
                  ref={(el) => !item.merged && addToRefs(el, idx)}
                  className={`w-full ${
                    !topButtons && 'md:w-[calc(100%-2.5rem)]'
                  } overflow-hidden mt-2`}
                >
                  {isModelProviderError(item) ? (
                    <ModelProviderError
                      item={item}
                      modelsIds={items.map((i) => i.modelRef)}
                    />
                  ) : (
                    <>
                      <ContentMessage
                        ref={(el) => {
                          if (item._id) messageRefs.current[item._id] = el;
                        }}
                        message={
                          item?.data?.role !== 'user' && search
                            ? truncateMessage(item?.data?.content, search)
                            : item?.data?.content
                        }
                        messageId={item._id}
                        userType={item?.data?.role}
                        onFavoriteFile={addReferenceFile}
                        onOpenFull={onOpenFull}
                        originalPrompt={item?.data?.originalPrompt}
                        expanded={isExpanded(idx)}
                        optimizing={optimizing}
                        videos={item.videoURL}
                        highlight={
                          mergeState == MERGE_STATES.HIGHLIGHT &&
                          isMerging(item)
                        }
                        highlights={handleCurrentsHighlights()}
                        images={item?.imagesURL?.map((image) => ({
                          ...image,
                          loading: loading,
                        }))}
                        store={store}
                        renderedFromShareChatPage={false}
                        isEditable={false}
                        onSend={onSend}
                        loading={loading}
                        capability={capability}
                        functionCapability={item?.data?.function}
                        reasoning={item?.data?.reasoning}
                        annotations={item?.data?.annotations}
                      />
                      {item?.data?.role &&
                        item?.data?.role !== 'user' &&
                        topButtons &&
                        !loading && (
                          <div
                            className="text-xs mt-2 flex justify-center
                              md:absolute md:bottom-2 md:right-4 md:justify-en md:invisible md:group-hover:visible"
                            style={{ color: '#858585' }}
                          >
                            {moment(item.date).format('MMM DD YYYY, HH:mm:ss')}
                          </div>
                        )}
                    </>
                  )}
                </div>
              ) : (
                <div className="w-full">
                  <div className="mt-3">
                    <TextAreaEditMessage
                      value={editValue}
                      onChange={handleChange}
                      placeholder=""
                      classNameContainer={` outline-none focus:outline-none text-md bg-white rounded w-full font-regular ${
                        false ? 'border-red-400' : 'border-cool-gray'
                      } font-figtree text-raisin-black shadow-none text-[16px]`}
                      classNameMainContainer={'bg-white'}
                      autoFocus
                      minRows={1}
                      maxRows={9}
                      highlightWords={highlightWordsTextAreaV2}
                    >
                      <div className="flex gap-3 px-[0.625rem]">
                        {imagesPreview.length > 0 &&
                          imagesPreview.map((image, index) => (
                            <div
                              key={index}
                              className="mt-3 relative cursor-pointer"
                              onMouseEnter={() => handleMouseEnter(index)}
                              onMouseLeave={handleMouseLeave}
                            >
                              <img
                                src={image.image_url.url}
                                alt={'Preview-' + index}
                                className="w-16 h-16 rounded object-cover"
                              />
                              <div
                                className={`absolute -top-[0.625rem] -right-[0.625rem] ${
                                  hoveredIndex === index
                                    ? 'visible'
                                    : 'invisible'
                                }`}
                              >
                                <XCircleIcon
                                  className={`w-5 h-5 ${
                                    themeValue === 'dark'
                                      ? 'dark:text-quicksilver'
                                      : 'text-cool-gray'
                                  }`}
                                  onClick={() => removeImage(image)}
                                />
                              </div>
                            </div>
                          ))}
                      </div>
                    </TextAreaEditMessage>
                  </div>

                  {selectedModels.length > 0 && (
                    <div className="flex gap-1 mt-1 font-figtree text-xs items-center justify-end">
                      <div
                        className={`${
                          themeValue === 'dark'
                            ? 'dark:text-sonic-silver '
                            : 'text-cool-gray '
                        } font-light`}
                      >
                        <span
                          className={`${
                            words > editModelSelected.words_text &&
                            'text-red-600'
                          }`}
                        >
                          {words.toLocaleString('en-US')}
                        </span>
                        /{editModelSelected.words_text.toLocaleString('en-US')}
                      </div>
                      <a
                        href="https://straico.com/multimodel/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="rounded-full cursor-pointer relative group flex flex-col items-center group text-cool-gray dark:text-sonic-silver">
                          <InformationCircleIcon className="w-4 h-4" />
                        </div>
                      </a>
                      <div className="flex flex-row items-center px-1 bg-cool-gray dark:bg-sonic-silver rounded-[20px] text-white font-semibold p-[2px]">
                        <StraiCoinIcon className="w-4 h-4" />
                        <span className="mx-1">
                          {calculateCoinsEdit(words, imagesPreview)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end space-x-2 mt-3 my-1">
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleCancel}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleSend(item)}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Send
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {loading && <div className="h-8"></div>}
            {!topButtons && (
              <div className="w-full flex items-end justify-start">
                {getButtons(item, topButtons)}
                {tabButtons && (
                  <>
                    <div className="flex-1"></div>
                    {tabButtons}
                  </>
                )}
              </div>
            )}
            <DeleteMessage
              open={showDeleteMessageDialog}
              onClose={() => setShowDeleteMessageDialog(false)}
              onDelete={handleConfirmDelete}
              deleted={deleted}
              setDeleted={setDeleted}
              title={deleteTitle}
              message={deleteMessage}
            />
          </>
        );
      };

      const getButtons = (item, sideBySide) => {
        return (
          <>
            {!editSelected && (
              <>
                {!readOnlyMessage && !loading && (
                  <Tooltip title="Text to speech" arrow>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMessageForTTS(item);
                      }}
                    >
                      <SpeakerWaveIcon className="h-4 w-4 text-battleship-gray" />
                    </IconButton>
                  </Tooltip>
                )}

                {!readOnlyMessage &&
                  !loading &&
                  availableDownloadTypes?.length > 0 && (
                    <Tooltip
                      title={`Download as ${availableDownloadTypes.join(
                        ' or '
                      )}`}
                      arrow
                    >
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setMessageToDownload(item);
                          setIsDownloadDialogOpen(true);
                        }}
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 text-battleship-gray" />
                      </IconButton>
                    </Tooltip>
                  )}

                {!loading && (
                  <Tooltip title="Copy markdown" arrow>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(item);
                      }}
                    >
                      <ClipboardDocumentIcon className="h-4 w-4 text-battleship-gray" />
                    </IconButton>
                  </Tooltip>
                )}

                {!readOnlyMessage &&
                  isUser() &&
                  !isReadOnly(item) &&
                  !loading && (
                    <Tooltip title="Edit message" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPrompt(item);
                        }}
                      >
                        <span className="flex items-center justify-center h-4 w-4">
                          <PencilSquareIcon
                            className="text-battleship-gray"
                            strokeWidth={1.3}
                            style={{ width: '14px', height: '14px' }}
                          />
                        </span>
                      </IconButton>
                    </Tooltip>
                  )}

                {/* Delete message */}
                {!readOnlyMessage &&
                  item?._id &&
                  !loading &&
                  !sideBySide &&
                  !isUser() && (
                    <Tooltip title="Delete message" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item?._id);
                        }}
                      >
                        <TrashIcon className="h-4 w-4 text-battleship-gray" />
                      </IconButton>
                    </Tooltip>
                  )}

                {/* Delete message, for side by side format */}
                {!readOnlyMessage &&
                  item?._id &&
                  !loading &&
                  sideBySide &&
                  !isUser() && (
                    <Tooltip title="Delete message" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item?._id);
                        }}
                      >
                        <TrashIcon className="h-4 w-4 text-battleship-gray" />
                      </IconButton>
                    </Tooltip>
                  )}

                {/* Delete **response**, for side by side format */}
                {!readOnlyMessage &&
                  item?._id &&
                  !loading &&
                  sideBySide &&
                  !isSourceForSelectedMerge(item) && (
                    <Tooltip title="Delete response" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item._id, showDeleteResponse());
                        }}
                      >
                        <XCircleIconOutline className="h-4 w-4 text-battleship-gray" />
                      </IconButton>
                    </Tooltip>
                  )}

                {/* Delete **response**, disabled when it serves as source for selected merge */}
                {!readOnlyMessage &&
                  item?._id &&
                  !loading &&
                  sideBySide &&
                  isSourceForSelectedMerge(item) && (
                    <Tooltip
                      title="Cannot delete this response because it's currently being used as a source for merge. Please delete the merged response first, then you can delete this response."
                      arrow
                    >
                      <span>
                        <IconButton
                          disabled
                          sx={{
                            '&.Mui-disabled': {
                              color: 'rgba(156, 163, 175, 0.5)',
                            },
                          }}
                        >
                          <XCircleIconOutline className="h-4 w-4" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                {/* Mark as favorite */}
                {!readOnlyMessage &&
                  isSelected(item) &&
                  !favorite &&
                  !loading && (
                    <Tooltip title="Add to Favorities" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(item, true);
                        }}
                      >
                        <StarIconOutline className="h-4 w-4 text-battleship-gray" />
                      </IconButton>
                    </Tooltip>
                  )}

                {/* Unmark as favorite */}
                {!readOnlyMessage &&
                  isSelected(item) &&
                  favorite &&
                  !loading && (
                    <Tooltip title="Remove from Favorities" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(item, false);
                        }}
                      >
                        <StarIconSolid
                          className={`h-4 w-4 ${
                            themeValue === 'dark'
                              ? 'dark:text-yellow-400'
                              : 'text-yellow-500'
                          }`}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                {!sideBySide && !readOnlyMessage && !loading && index === 0 && (
                  <>
                    <Tooltip title="Regenerate response" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onRegenerate();
                        }}
                      >
                        <ArrowPathIcon className="h-4 w-4 text-battleship-gray" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Continue response" arrow>
                      <IconButton
                        onClick={(e) => {
                          if (checkErrorSelected(item)) {
                            return;
                          }
                          e.stopPropagation();
                          continueResponse();
                        }}
                      >
                        <ArrowRightIcon className="h-4 w-4 text-battleship-gray" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                {item?.data?.role !== 'user' && !sideBySide && (
                  <div
                    className="absolute bottom-5 right-6 text-xs text-gray-400"
                    style={{ color: '#858585' }}
                  >
                    {moment(item.date).format('MMM DD YYYY, HH:mm:ss')}
                  </div>
                )}

                {item?.data?.originalPrompt && !loading && (
                  <div className="absolute bottom-2 right-10 z-20">
                    <PromptOptions
                      store={store}
                      loading={loading}
                      originalPrompt={item?.data?.originalPrompt}
                      optimizedPrompt={item?.data?.content}
                      basePrompt={item?.data?.basePrompt}
                      systemInstructions={item?.data?.systemInstructions}
                      onUndoOptimization={(e) => {
                        e.stopPropagation();
                        onRegenerateEdit(
                          item?.data?.originalPrompt,
                          item?.date,
                          chat,
                          [],
                          item?.data?.originalPrompt.split(/\s+/).length,
                          JSON.parse(JSON.stringify(chat)),
                          true
                        );
                      }}
                      conversationId={conversationId}
                    />
                  </div>
                )}
              </>
            )}
          </>
        );
      };

      const getButtonsMerge = (item) => {
        return (
          <>
            <Tooltip title="Copy markdown" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(item);
                }}
              >
                <ClipboardDocumentIcon className="h-4 w-4 text-battleship-gray" />
              </IconButton>
            </Tooltip>

            {isMerging(item) && (
              <>
                {' '}
                <Tooltip title="Unhighlight all" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item._id)
                        messageRefs.current[item._id].removeHighlights();
                    }}
                  >
                    <PaintBrushIcon className="h-4 w-4 text-peachy-maroney" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Highlight all" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item._id)
                        messageRefs.current[item._id]?.highlightAll();
                    }}
                  >
                    <PaintBrushIconSolid className="h-4 w-4 text-peachy-maroney" />
                  </IconButton>
                </Tooltip>{' '}
              </>
            )}

            {isMerging(item) ? (
              <Tooltip title="Remove from merge" arrow>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    updateMessages(item._id, MESSAGE_ACTIONS.REMOVE);
                    messageRefs.current[item._id].removeHighlights();
                  }}
                >
                  <MinusCircleIcon className="h-4 w-4 text-peachy-maroney" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Add to merge" arrow>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    updateMessages(item._id, MESSAGE_ACTIONS.ADD);
                    messageRefs.current[item._id]?.highlightAll();
                  }}
                >
                  <PlusCircleIcon className="h-4 w-4 text-peachy-maroney" />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      };

      const showDeleteResponse = () => {
        if (!Array.isArray(item)) return false;

        const filterArray = item.map((res) => res.hidden !== true);
        return filterArray.length > 1;
      };

      const getIcon = (icon, className = 'h-6 w-6') => {
        const Icon = get(outlineIcons, icon);
        return Icon ? <Icon className={className} /> : null;
      };

      let capability = null;

      useEffect(() => {
        getCompatibleModels().then((models) => setCompatibleModels(models));
      }, [store.api]);

      const getCompatibleModels = async () => {
        try {
          const response = await store.api.get(`/capability/models`);
          return response.data.models;
        } catch (error) {
          console.error(error);
          return [];
        }
      };

      const [localCapabilities, setLocalCapabilities] = useState(() => {
        const savedCapabilities = localStorage.getItem('capabilities');
        return savedCapabilities ? JSON.parse(savedCapabilities) : [];
      });

      const [isLabelExpanded, setIsLabelExpanded] = useState(false);

      useEffect(() => {
        const state = item?.data?.function?.state;

        if (typeof onLoading === 'function') {
          if (state === 'finish') {
            // Cuando termina, desactivamos el loading independientemente de si hay imágenes o no
            onLoading(false);
          } else if (state !== undefined) {
            onLoading(true);
          }
        }
      }, [item?.data?.function?.state, onLoading]);

      const getCapabilityState = (func, item) => {
        if (!item) return;

        if (!capabilities || !Array.isArray(capabilities)) {
          return;
        }
        if (isReadOnly(item)) {
          return;
        }
        if (!localCapabilities || localCapabilities.length === 0) {
          return null;
        }
        if (func) {
          const capability = capabilities.find(
            (cap) => cap._id === func.capability
          );
          return capability ? (
            capability.type !== 'itv' ? (
              func.state === 'finish' && capability.type !== 'browser' ? (
                // Si el estado es 'finish', verificamos si deberíamos tener imágenes
                // para modelos de generación de imágenes
                capability.type === 'image-generation' &&
                (!item?.imagesURL || item?.imagesURL.length === 0) ? (
                  <div className="flex rounded py-1 px-2 mt-2 gap-2 bg-lavender text-violet-blue dark:text-tropical-indigo dark:bg-ship-grey w-fit text-xs items-center">
                    <span>
                      Oops, it looks like there was an issue with the image in
                      your request.
                    </span>
                  </div>
                ) : (
                  <div className="flex rounded py-1 px-2 mt-2 gap-2 bg-lavender text-violet-blue dark:text-tropical-indigo dark:bg-ship-grey w-fit text-xs items-center">
                    {getIcon(capability.icon, 'h-4 w-4')}
                    <span>{capability.label_states[func.state]}</span>
                  </div>
                )
              ) : (
                <div className="flex rounded py-1 px-2 mt-2 gap-2 bg-lavender text-violet-blue dark:text-tropical-indigo dark:bg-ship-grey w-fit text-xs items-center">
                  {getIcon(capability.icon, 'h-4 w-4')}
                  <span>{capability.label_states[func.state]}</span>
                </div>
              )
            ) : (
              <div className="flex rounded py-1 px-2 mt-2 gap-2 bg-lavender text-violet-blue dark:text-tropical-indigo dark:bg-ship-grey w-fit text-xs items-center">
                {getIcon(capability.icon, 'h-4 w-4')}
                <span>{capability.label_states[func.state]}</span>
              </div>
            )
          ) : null;
        } else if (!func && item?.data?.role !== 'user' && !isReadOnly(item)) {
          if (compatibleModels.length === 0) {
            return;
          }
          const isCompatible = compatibleModels.includes(item?.modelRef);
          const storedCapabilitiesString = localStorage.getItem('capabilities');

          const storedCapabilities = storedCapabilitiesString
            ? JSON.parse(storedCapabilitiesString)
            : [];

          const incompatibleCapabilities = storedCapabilities.filter(
            (storedCap) =>
              capabilities.some((cap) => cap.name === storedCap.name)
          );

          if (!isCompatible && incompatibleCapabilities.length > 0) {
            const incompatibleNames = incompatibleCapabilities
              .map((cap) => cap.name)
              .join(', ');
            return (
              <div className="flex rounded py-1 px-2 mt-2 gap-2 bg-lavender text-violet-blue dark:text-tropical-indigo dark:bg-ship-grey w-fit text-xs items-center">
                <ExclamationCircleIcon className="h-4 w-4" />
                <div className="flex items-center gap-2">
                  <span>
                    {isLabelExpanded
                      ? `The current model isn't compatible with the selected capabilities: ${incompatibleNames}`
                      : `The current model isn't compatible with the selected capabilities...`}
                  </span>
                  <ChevronDownIcon
                    onClick={() => setIsLabelExpanded(!isLabelExpanded)}
                    className={`h-4 w-4 cursor-pointer transition-transform ${
                      isLabelExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>
            );
          } else if (isCompatible) {
            if (item?.data?.content !== null && item?.imagesURL?.length === 0) {
              return;
            }
            return (
              <div className="flex rounded py-1 px-2 mt-2 gap-2 bg-lavender text-violet-blue dark:text-tropical-indigo dark:bg-ship-grey w-fit text-xs items-center">
                <ArrowPathIcon className="h-4 w-4" />
                <span>Checking capabilities calls...</span>
              </div>
            );
          }

          return (
            <div className="flex rounded py-1 px-2 mt-2 gap-2 bg-lavender text-violet-blue dark:text-tropical-indigo dark:bg-ship-grey w-fit text-xs items-center">
              <SparklesIcon className="h-4 w-4" />
              <span>
                It looks like you attempted to use capabilities with this model,
                but it's not compatible
              </span>
            </div>
          );
        }
      };

      const isHidden = (item) => {
        return Array.isArray(item)
          ? item.some((ele) => ele?.hidden)
          : item?.hidden;
      };

      const checkCapability = (ele) => {
        if (!ele?.data?.function) {
          return;
        }

        const capability = Array.isArray(capabilities)
          ? capabilities.find(
              (cap) => cap._id === ele?.data?.function?.capability
            )
          : null;

        return (
          capability && (
            <div className="relative">
              <div className="group/capability">
                {getIcon(capability.icon, 'h-4 h-4')}
                <Tooltip className="hidden absolute font-medium flex-col items-center group-hover/capability:flex left-[calc(100%_-_0.5rem)] top-1/2 z-50">
                  <div className="p-2 text-sm leading-none text-raisin-black bg-lavender bg-opacity-25 shadow-lg text-center backdrop-filter backdrop-blur rounded-md">
                    Capability:{' '}
                    <span className="font-bold break-words whitespace-normal">
                      {capability.name}
                    </span>
                  </div>
                </Tooltip>
              </div>
            </div>
          )
        );
      };

      const getModelIcon = () => {
        let currentModel;
        if (!Array.isArray(item)) {
          currentModel = item;
        } else {
          currentModel = modelSelected
            ? item.find((e) => e.modelRef === modelSelected)
            : item.find((e) => e.selected);
        }
        //"Dall-E 3"
        if (currentModel && currentModel.model === 'Dall-E 3') {
          const gptIcon = modelsList.find(
            (m) => m.model === 'gpt-4o-2024-08-06'
          );
          const GptIconComponent =
            gptIcon && gptIcon.icon ? get(modelIcons, gptIcon.icon) : null;
          return GptIconComponent ? (
            <GptIconComponent className="h-10 w-10" />
          ) : (
            <ModelsIcon className="h-10 w-10 stroke-cool-gray" />
          );
        }

        let icons = [];
        if (currentModel) {
          if (Array.isArray(currentModel.modelRef)) {
            icons = currentModel.modelRef
              .map((ref) => {
                const icon = modelsList.find((m) => m._id === ref);
                return icon && icon.icon ? get(modelIcons, icon.icon) : null;
              })
              .filter(Boolean);
          } else {
            const icon = modelsList.find(
              (m) => m._id === currentModel.modelRef
            );
            if (icon && icon.icon) {
              icons.push(get(modelIcons, icon.icon));
            }
          }
        }

        return icons.length > 0 ? (
          <div className="flex flex-col gap-2">
            {icons.map((Icon, index) => (
              <Icon key={index} className="h-10 w-10" />
            ))}
          </div>
        ) : (
          <ModelsIcon className="h-10 w-10 stroke-cool-gray" />
        );
      };

      const getModels = async () => {
        try {
          const response = await store.api.get(`/model`);
          setModelsList(response.data.models);
        } catch (error) {
          console.error(error);
        }
      };

      const getContentMessage = () => {
        if (isUser()) return defaultView();
        switch (renderType) {
          case COLUMNS:
          case ROWS:
          case GRID:
            return SideBySideView();
          default:
            return defaultView();
        }
      };

      const getGridCols = () => {
        const responseItems = items.filter((i) => !i.merged);
        switch (renderType) {
          case COLUMNS:
            return 'grid-cols-' + responseItems.length;
          case GRID:
            return responseItems.length >= 2 ? 'grid-cols-2' : '';
          default:
            return 'grid-cols-1';
        }
      };

      const isOdd = (index) => {
        if (!Array.isArray(item)) return false;

        const responses = item.filter((i) => !i.merged);
        const total = item.filter((i) => !i.merged).length - 1;
        return index == total && responses.length % 2 !== 0;
      };

      const isSingleResponse = () => {
        return (
          !Array.isArray(item) ||
          (Array.isArray(item) && item.filter((e) => !e.merged).length == 1)
        );
      };

      useEffect(() => {
        const newItems = getTabs(item);

        if (itemOrder.length > 0) {
          const orderedItems = [...newItems].sort((a, b) => {
            const indexA = itemOrder.findIndex((id) => id === a._id);
            const indexB = itemOrder.findIndex((id) => id === b._id);
            return indexA - indexB;
          });
          setItems(orderedItems);
        } else {
          setItems(newItems);
        }
      }, [item, changeSelection]);

      const isMergeSelected = () => {
        if (!Array.isArray(item)) return false;
        return item.filter((i) => i.merged).some((i) => i.selected);
      };

      const getFirstWord = (text) => {
        return text.split(' ')[0];
      };

      const getMergeTab = (active) => {
        return items.filter((i) => i.merged)[active];
      };

      const defaultView = () => {
        const mergeTab = getMergeTab(activeMerge);

        const topBarId = isUser()
          ? `user-top-bar-idx-${messageIndexForDots}`
          : null;

        return (
          <div
            className={`pb-1 pt-0 ${
              index !== 0 && renderType === TABS && 'border-b border-cool-gray'
            }`}
          >
            {/* Add the top bar for connection target on user messages in COLUMNS view */}
            {isUser() && renderType === COLUMNS && (
              <div
                // Use callback ref to register the top bar node
                ref={(node) =>
                  registerCardRef && registerCardRef(topBarId, node)
                }
                // Use border instead of background for a line appearance
                className="w-full border-t-2 border-nue-blue rounded-t-lg relative"
              ></div>
            )}

            <div className="w-full flex pt-2 justify-between">
              <div className="flex flex-1 flex-wrap gap-2">
                {items
                  .filter((i) => !i.merged)
                  .map((ele, idx) => (
                    <div className="relative" key={idx}>
                      {ele?.data?.role !== 'user' && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            isSelected(ele) ? null : handleOutput(ele, idx);
                          }}
                          className={`${
                            ele?.finished === false && 'relative'
                          } overflow-visible w-fit capitalize rounded-md px-1 text-[12px] ${
                            isModelProviderError(ele)
                              ? 'bg-brick-yellow text-white'
                              : isSelected(ele)
                              ? 'bg-nue-blue text-white'
                              : 'bg-platinum text-cool-gray dark:bg-tornado-cloud dark:text-powder-blue'
                          } font-bold cursor-pointer select-none flex gap-1 items-center`}
                        >
                          {/* Show SVG logo only for selected response and only on mobile */}
                          {isSelected(ele) && (
                            <>
                              <span className="inline-block sm:hidden align-middle mr-1">
                                <TreeLogo
                                  width={16}
                                  height={14}
                                  className="inline-block align-middle"
                                />
                              </span>
                            </>
                          )}
                          <p>{ele?.model}</p>
                          {checkCapability(ele)}
                          {ele?.finished === false && (
                            <div
                              className={`absolute inset-0 transform -translate-x-full bg-gradient-to-r ${
                                isSelected(ele)
                                  ? 'from-white to-white via-lavender'
                                  : 'from-ship-grey to-nue-blue via-violet-blue'
                              } animate-flash`}
                            ></div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                {items.some((i) => i.merged) && (
                  <div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        isMergeSelected()
                          ? null
                          : handleOutput(mergeTab, 'merge-' + activeMerge);
                      }}
                      className={` overflow-hidden w-fit capitalize rounded-md px-1 text-[12px] ${
                        isMergeSelected()
                          ? 'bg-peachy-maroney text-white'
                          : 'bg-platinum text-cool-gray dark:bg-tornado-cloud dark:text-powder-blue'
                      } font-bold cursor-pointer select-none flex gap-1 items-center`}
                    >
                      <p className="capitalize">
                        {mergeTab.merge_metadata
                          ? mergeTab.merge_metadata.operation.name
                          : 'Unify responses'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="chat-container">
              <div
                ref={chatContainerRef}
                className={`w-full ${isUser() ? 'pl-7' : ''}`}
              >
                {items
                  .filter((i) => !i.merged)
                  .map((ele, index) => (
                    <div key={index}>
                      {isSelected(ele) && (
                        <>
                          {ChatBubbleContent(ele, deleted, index)}
                          {ele.finished === false &&
                            ele.data.role !== 'user' && <Skeleton />}
                        </>
                      )}
                    </div>
                  ))}
                <div>{isMergeSelected() && getMergeCard(items)}</div>
              </div>
            </div>
            {/* Add the connection dots container for user messages in COLUMNS view */}
            {isUser() &&
              renderType === COLUMNS &&
              subsequentResponseCount > 0 && (
                <div
                  className={`py-1.5 rounded-b-3xl flex justify-center items-center relative mt-1 w-full ${
                    themeValue === 'light' ? 'bg-gray-200' : 'bg-gray-700'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isLatestUserMessage &&
                      !subsequentResponsesHaveSpecificCapabilities
                        ? ''
                        : 'space-x-8'
                    }`}
                  >
                    {[...Array(subsequentResponseCount)].map(
                      (_, visualIndex) => {
                        // Use custom dot order if available, otherwise use default order
                        const originalDotIndex = customDotOrder
                          ? customDotOrder[visualIndex]
                          : visualIndex;

                        const isActive =
                          activeSubsequentDotIndices &&
                          activeSubsequentDotIndices.includes(originalDotIndex);
                        const dotId = `dot-${messageIndexForDots}-${originalDotIndex}`;
                        const activeColorClass = 'border-nue-blue';
                        const inactiveColorClass =
                          themeValue === 'light'
                            ? 'border-gray-400'
                            : 'border-gray-600';
                        const activeFillClass = 'bg-nue-blue';
                        const inactiveFillClass =
                          themeValue === 'light'
                            ? 'bg-gray-400'
                            : 'bg-gray-600';

                        return (
                          <div key={dotId} className="flex items-center">
                            {/* Dot */}
                            <div
                              ref={(node) =>
                                registerDotRef && registerDotRef(dotId, node)
                              }
                              className="relative cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onDotClick) {
                                  onDotClick(
                                    messageIndexForDots,
                                    originalDotIndex
                                  );
                                }
                              }}
                            >
                              {/* Outer Border */}
                              <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors border-2 ${
                                  isActive
                                    ? activeColorClass
                                    : inactiveColorClass
                                }`}
                              >
                                {/* Inner Fill */}
                                <div
                                  className={`w-3 h-3 rounded-full transition-colors ${
                                    isActive
                                      ? activeFillClass
                                      : inactiveFillClass
                                  }`}
                                ></div>
                              </div>
                            </div>

                            {/* SmartMerge Ring - only show between dots (not after the last dot) and only for latest user message */}
                            {visualIndex < subsequentResponseCount - 1 &&
                              isLatestUserMessage &&
                              !subsequentResponsesHaveSpecificCapabilities && (
                                <div
                                  className="mx-4 cursor-pointer hover:scale-110 hover:opacity-80 transition-all duration-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isUser() && onRingClickFromUser) {
                                      // User message: send signal to ListingChatMessages
                                      onRingClickFromUser(
                                        messageIndexForDots,
                                        visualIndex
                                      );
                                    } else if (!isUser()) {
                                      // AI response: handle locally
                                      handleRingClick(visualIndex);
                                    }
                                  }}
                                  title="Merge adjacent responses"
                                >
                                  <SmartMergeDoubleRing />
                                </div>
                              )}
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}
            <style>
              {`
                .latex-block {
                  display: block;
                  margin: 1em auto;
                  text-align: center;
                  overflow-x: auto;
                  padding: 10px;
                  width: 92%;
                }

                .latex-inline {
                  display: inline;
                  margin: 0;
                  overflow-x: visible;
                }

                .latex-block::-webkit-scrollbar-thumb {
                  border-radius: 10px;
                }

              `}
            </style>
          </div>
        );
      };

      const sensors = useSensors(
        useSensor(isMobile ? TouchSensor : PointerSensor, {
          activationConstraint: {
            ...(isMobile
              ? { delay: 150, tolerance: 5, distance: 10 }
              : { delay: 300, tolerance: 5, distance: isDragging ? 1 : 1000 }),
          },
        })
      );

      const modifiers = useMemo(
        () => [
          (args) => {
            const { transform, draggingNodeRect } = args;

            const containerElement = chatContainerRef.current;
            if (!containerElement || !draggingNodeRect) {
              return transform;
            }

            const containerRect = containerElement.getBoundingClientRect();

            const minX = containerRect.left - draggingNodeRect.left;
            const maxX = containerRect.right - draggingNodeRect.right;
            const minY = containerRect.top - draggingNodeRect.top;
            const maxY = containerRect.bottom - draggingNodeRect.bottom;

            return {
              ...transform,
              x: isMobile ? 0 : Math.max(minX, Math.min(maxX, transform.x)),
              y: Math.max(minY, Math.min(maxY, transform.y)),
            };
          },
        ],
        []
      );

      const getDragOverlay = (active, over) => {
        if (!over) return null;

        const activeElement = document.querySelector(
          `[data-id="${active.id}"]`
        );

        if (!activeElement) return null;

        const activeRect = activeElement.getBoundingClientRect();
        const overRect = over.rect;

        const activeCenter = {
          x: activeRect.left + activeRect.width / 2,
          y: activeRect.top + activeRect.height / 2,
        };

        const overCenter = {
          x: overRect.left + overRect.width / 2,
          y: overRect.top + overRect.height / 2,
        };

        const distance = Math.sqrt(
          Math.pow(activeCenter.x - overCenter.x, 2) +
            Math.pow(activeCenter.y - overCenter.y, 2)
        );

        const DISTANCE_THRESHOLD = overRect.width * 0.25;
        const isCombining = distance < DISTANCE_THRESHOLD;

        return {
          isCombining,
          targetId: over.id,
        };
      };

      const SideBySideView = () => {
        const handleDragStartCard = (event) => {
          setActiveId(event.active.id);
        };

        const handleDragMoveCard = (event) => {
          const { active, over } = event;
          const overlay = getDragOverlay(active, over);
          setDragOverlay(overlay);

          if (active.rect.current && isMobile) {
            const containerEl = document.querySelector('.overflow-auto');
            if (!containerEl) return;

            const containerRect = containerEl.getBoundingClientRect();
            const activeRect = active.rect.current.translated;
            const scrollThreshold = containerRect.height * 0.2;

            const containerTop = Math.max(containerRect.top, 0);
            const containerBottom = Math.min(
              containerRect.bottom,
              window.innerHeight
            );

            const needsUpScroll =
              activeRect.top >= containerTop &&
              activeRect.top < containerTop + scrollThreshold;

            const needsDownScroll =
              activeRect.bottom <= containerBottom &&
              activeRect.bottom > containerBottom - scrollThreshold;

            if (needsUpScroll) {
              const distance = containerTop + scrollThreshold - activeRect.top;
              const scrollAmount = Math.min(
                500,
                (distance / scrollThreshold) * 500
              );
              onScrollRequest(-1, scrollAmount);
            } else if (needsDownScroll) {
              const distance =
                activeRect.bottom - (containerBottom - scrollThreshold);
              const scrollAmount = Math.min(
                500,
                (distance / scrollThreshold) * 500
              );
              onScrollRequest(1, scrollAmount);
            }
          }
        };

        const handleDragOverCard = (event) => {
          const { active, over } = event;
          if (!over) return;

          if (active.id !== over.id) {
            const overlay = getDragOverlay(active, over);
            setDragOverlay(overlay);
          }
        };

        const isNewMerge = (active, over) => {
          return (
            (mergeActive &&
              !messages.includes(active) &&
              !messages.includes(over)) ||
            !mergeActive
          );
        };

        const handleDragEndCard = (event) => {
          const { active, over } = event;

          if (over && active.id !== over.id) {
            const activeId = active.data.current.ele._id;
            const overId = over.data.current.ele._id;

            if (dragOverlay.isCombining) {
              if (isNewMerge(activeId, overId)) {
                setActive(true);
                setMergeItem(item);

                // Check over
                const overItem = items.find((i) => i._id == overId);
                if (overItem && !isModelProviderError(overItem)) {
                  updateMessages(overId, MESSAGE_ACTIONS.ADD);
                  messageRefs.current[overId]?.highlightAll();
                }
                // Check active
                const activeItem = items.find((i) => i._id == activeId);
                if (activeItem && !isModelProviderError(activeItem)) {
                  updateMessages(activeId, MESSAGE_ACTIONS.ADD);
                  messageRefs.current[activeId]?.highlightAll();
                }
              } else {
                if (!messages.includes(overId)) {
                  updateMessages(overId, MESSAGE_ACTIONS.ADD);
                  messageRefs.current[overId]?.highlightAll();
                }

                if (!messages.includes(activeId)) {
                  updateMessages(activeId, MESSAGE_ACTIONS.ADD);
                  messageRefs.current[activeId]?.highlightAll();
                }

                setActiveTab(MERGE_STATES.HIGHLIGHT);
              }
            } else {
              const oldIndex = items.findIndex((item) => item._id === activeId);
              const newIndex = items.findIndex((item) => item._id === overId);

              const newItems = [...items];
              const [movedItem] = newItems.splice(oldIndex, 1);
              newItems.splice(newIndex, 0, movedItem);

              setItems(newItems);
              setItemOrder(newItems.map((item) => item._id));

              // Redraw Tree-view connections for this interaction
              if (onCardReorder && renderType === COLUMNS) {
                // Pass the new order of items to ListingChatMessages
                const newOrder = newItems.map((item) => item._id);
                onCardReorder(index, newOrder);
              }
            }
          }

          setActiveId(null);
          setDragOverlay(null);
        };

        const disableDragging = (ele, index) => {
          return (
            (mergeState == MERGE_STATES.HIGHLIGHT && isMerging(ele)) ||
            loading ||
            (isMobile && !expandedIdx.includes(index))
          );
        };

        return (
          <div className="pb-1 pt-0 h-full">
            <div className="chat-container flex flex-col gap-3 justify-start w-full h-full">
              {!collapsed && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStartCard}
                  onDragMove={handleDragMoveCard}
                  onDragOver={handleDragOverCard}
                  onDragEnd={handleDragEndCard}
                  modifiers={modifiers}
                  autoScroll={false}
                >
                  <div
                    ref={chatContainerRef}
                    className={`relative w-full h-full grid gap-5 scrollbar-hidden ${getGridCols()} py-5 md:px-5`}
                    style={{
                      whiteSpace: 'normal',
                    }}
                  >
                    {items
                      .filter((i) => !i.merged)
                      .map((ele, index) => {
                        const cardId = `card-${ele._id}`;
                        return (
                          <motion.div
                            ref={(node) => {
                              if (registerCardRef) {
                                registerCardRef(cardId, node);
                              }
                            }}
                            className={`rounded-[22px] h-full ${
                              activeId !== ele._id &&
                              'shadow-[4px_4px_10px_0px_rgba(0,0,0,0.10)]'
                            } ${
                              renderType === GRID &&
                              isOdd(index) &&
                              'col-span-2'
                            }`}
                            key={ele._id || index}
                            layout="position"
                            transition={{
                              layout: {
                                duration: 0.3,
                                type: 'spring',
                                bounce: 0,
                              },
                            }}
                          >
                            <DroppableZone
                              ele={ele}
                              index={index}
                              dragOverlay={dragOverlay}
                            >
                              <DraggableTab
                                ele={ele}
                                index={index}
                                disabled={disableDragging(ele, index)}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    maxHeight: {
                                      xs: '100%',
                                      md: 'calc(100dvh - 300px)',
                                    },
                                    transition: 'all 0.3s ease-in-out',
                                  }}
                                  className={cn(
                                    `flex flex-col px-6 py-6 rounded-[22px] transition-all duration-300 ease-in-out overflow-auto group`,
                                    {
                                      'bg-white':
                                        themeValue === 'light' &&
                                        !isModelProviderError(ele),
                                      'bg-night-black':
                                        themeValue === 'dark' &&
                                        !isModelProviderError(ele),
                                      'bg-seasalt bg-gradient-to-b from-brick-yellow/30 to-brick-yellow/0':
                                        themeValue === 'light' &&
                                        isModelProviderError(ele),
                                      'bg-jet bg-gradient-to-b from-brick-yellow/30 to-brick-yellow/0':
                                        themeValue === 'dark' &&
                                        isModelProviderError(ele),
                                      'border-[3px] border-nue-blue':
                                        (isMerging(ele) ||
                                          isSourceForSelectedMerge(ele)) &&
                                        !isModelProviderError(ele),
                                      'border-[3px] border-nue-blue dark:border-nue-blue':
                                        isSelectedNoMerging(ele) &&
                                        !isModelProviderError(ele) &&
                                        !isSourceForSelectedMerge(ele),
                                      'border-[3px] border-seasalt':
                                        themeValue === 'light' &&
                                        isModelProviderError(ele),
                                      'border-[3px] border-night-black':
                                        themeValue === 'dark' &&
                                        isModelProviderError(ele),
                                      'cursor-default':
                                        isModelProviderError(ele),
                                    }
                                  )}
                                >
                                  {getCardContent(ele, index)}
                                </Box>
                              </DraggableTab>
                            </DroppableZone>
                          </motion.div>
                        );
                      })}
                  </div>
                </DndContext>
              )}
              <div
                className={`pb-2 pr-2 mt-10 ${
                  shouldAlignMergeCardRight(items)
                    ? 'flex justify-end z-20'
                    : ''
                }`}
              >
                {getMergeCard(items)}
              </div>
            </div>
          </div>
        );
      };

      const handleMouseEvents = (e, state) => {
        e.stopPropagation();
        setIsDragging(state);
        changeSensorsState(state);
      };

      const getCardContent = (ele, index) => {
        return (
          <>
            {/* Tabs */}
            <div className="relative">
              {ele?.data?.role !== 'user' && (
                <div
                  className={`flex items-center justify-between flex-wrap ${
                    !isModelProviderError(ele) && 'cursor-pointer'
                  } select-none -mb-2 md:mb-0`}
                  onClick={(e) => {
                    e.stopPropagation();
                    isSelected(ele) || isModelProviderError(ele)
                      ? null
                      : handleOutput(ele, index);
                  }}
                >
                  <div className="flex gap-1 items-center">
                    {renderType !== GRID && isModelProviderError(ele) && (
                      <div className="flex w-[18px] h-[18px] justify-center items-center text-sm font-bold bg-brick-yellow text-white rounded-full">
                        !
                      </div>
                    )}
                    <div
                      className={`${
                        ele?.finished === false && 'relative'
                      } overflow-visible w-fit capitalize rounded-md px-1 text-[12px] ${
                        isModelProviderError(ele)
                          ? 'bg-brick-yellow text-white'
                          : isSelectedNoMerging(ele) ||
                            isSourceForSelectedMerge(ele) // If selected or source of selected merge
                          ? 'bg-nue-blue text-white' // Purple
                          : themeValue === 'light'
                          ? 'bg-platinum text-cool-gray'
                          : 'bg-tornado-cloud text-powder-blue' // Default
                      } ${
                        isMerging(ele) && !isSourceForSelectedMerge(ele) // Only if actively merging but NOT a source of an already selected merge
                          ? '!bg-peachy-maroney !text-white'
                          : ''
                      } font-bold flex whitespace-nowrap gap-1 items-center mx-0 md:mx-0`}
                    >
                      {/* Show SVG logo only for selected response and only on mobile */}
                      {isSelected(ele) && (
                        <span className="inline-block sm:hidden align-middle mr-1">
                          <TreeLogo style={{ width: 16, height: 14 }} />
                        </span>
                      )}
                      <p>{ele?.model}</p>
                      {checkCapability(ele)}
                      {ele?.finished === false && (
                        <div
                          className={`absolute inset-0 transform -translate-x-full bg-gradient-to-r ${
                            isSelected(ele)
                              ? 'from-white to-white via-lavender'
                              : 'from-ship-grey to-nue-blue via-violet-blue'
                          } animate-flash`}
                        ></div>
                      )}
                    </div>
                  </div>
                  {/* Move icon */}
                  {!isMobile &&
                    !isMerging(ele) &&
                    !isSingleResponse() &&
                    !isModelProviderError(ele) && (
                      <div
                        className="hidden group-hover:flex py-2 px-3 items-center justify-center cursor-move"
                        onClick={(e) => e.stopPropagation()}
                        onMouseUp={(e) => handleMouseEvents(e, false)}
                        onMouseEnter={(e) => handleMouseEvents(e, true)}
                        onMouseDown={(e) => handleMouseEvents(e, true)}
                        onMouseLeave={(e) => handleMouseEvents(e, false)}
                      >
                        <Bars3Icon className="w-4 h-4 text-battleship-gray" />
                      </div>
                    )}
                  {!isModelProviderError(ele) && (
                    <>
                      <div className="hidden md:block">
                        {mergeActive && item == mergeItem
                          ? getButtonsMerge(ele)
                          : getButtons(ele, true)}
                      </div>
                      <div className="block md:hidden">
                        <IconButton onClick={() => handleExpanded(index)}>
                          <ChevronDownIcon
                            className={`h-4 w-4 text-battleship-gray duration-500 ${
                              expandedIdx.includes(index) && 'rotate-[180deg]'
                            }`}
                          />
                        </IconButton>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Content */}
            <div
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              className={`flex-1 overflow-auto overflow-rtl drag-handle cursor-text  ${
                themeValue === 'dark'
                  ? 'overflow-rtl-dark'
                  : 'overflow-rtl-light'
              }`}
              style={{ overflowX: 'hidden' }}
            >
              {ChatBubbleContent(ele, deleted, index, true)}
              {ele.finished === false && <Skeleton />}
            </div>

            {expandedIdx.includes(index) && (
              <div className="flex md:hidden justify-center">
                {mergeActive && item == mergeItem
                  ? getButtonsMerge(ele)
                  : getButtons(ele, true)}
              </div>
            )}
          </>
        );
      };

      // !TODO: TREE VIEW FUNCTIONS
      const shouldAlignMergeCardRight = (items) => {
        const mergeItems = items.filter((i) => i.merged);
        const nonMergedItems = items.filter((i) => !i.merged);

        // Only align right if:
        // 1. There are merge items (smart merge exists)
        // 2. There is a selected response that is not a merge item
        if (mergeItems.length === 0) return false;

        const hasSelectedNonMergeItem = nonMergedItems.some(
          (item) => isSelected(item) || isSelectedNoMerging(item)
        );

        return hasSelectedNonMergeItem;
      };

      const autoReorderSelectedToFirst = () => {
        const mergeItems = items.filter((i) => i.merged);
        const nonMergedItems = items.filter((i) => !i.merged);

        // Only reorder if:
        // 1. There are merge items (smart merge exists)
        // 2. There is a selected non-merge response
        // 3. The selected response is not already in first position
        if (mergeItems.length === 0) {
          return;
        }

        const selectedNonMergeItem = nonMergedItems.find(
          (item) => isSelected(item) || isSelectedNoMerging(item)
        );

        if (!selectedNonMergeItem) {
          return;
        }

        // Check if the selected item is already in first position
        const currentIndex = items.findIndex(
          (item) => item._id === selectedNonMergeItem._id
        );

        // Reorder items to move selected item to first position
        const newItems = [...items];
        const [movedItem] = newItems.splice(currentIndex, 1);
        newItems.splice(0, 0, movedItem);

        setItems(newItems);
        setItemOrder(newItems.map((item) => item._id));

        // Redraw Tree-view connections for this interaction
        if (onCardReorder && renderType === COLUMNS) {
          const newOrder = newItems.map((item) => item._id);
          onCardReorder(index, newOrder);
        }
      };

      // Auto-reorder cards to put selected non-merge response first
      useEffect(() => {
        // Only trigger reordering in Tree-view (COLUMNS) mode
        if (renderType === COLUMNS) {
          autoReorderSelectedToFirst();
        }
      }, [item, renderType]); // Watch for changes in item selection and render type

      // Handle SmartMerge ring click - initiates merge between two adjacent responses
      const handleRingClick = (ringIndex) => {
        // This function now only runs on AI responses, not user messages
        // Get the two adjacent responses based on ring position
        const nonMergedItems = items.filter((i) => !i.merged);
        const firstResponseIndex = ringIndex;
        const secondResponseIndex = ringIndex + 1;

        // Safety checks
        if (secondResponseIndex >= nonMergedItems.length) return;

        const firstResponse = nonMergedItems[firstResponseIndex];
        const secondResponse = nonMergedItems[secondResponseIndex];

        // Don't allow merge if either response has errors
        if (
          isModelProviderError(firstResponse) ||
          isModelProviderError(secondResponse)
        ) {
          return;
        }

        // Get the response IDs
        const firstResponseId = firstResponse._id;
        const secondResponseId = secondResponse._id;

        // Check if this is a new merge operation
        const isNewMergeOperation =
          (mergeActive &&
            !messages.includes(firstResponseId) &&
            !messages.includes(secondResponseId)) ||
          !mergeActive;

        if (isNewMergeOperation) {
          // Start a new merge operation
          setActive(true);
          setMergeItem(item);

          // Add both responses to the merge
          updateMessages(firstResponseId, MESSAGE_ACTIONS.ADD);
          messageRefs.current[firstResponseId]?.highlightAll();

          updateMessages(secondResponseId, MESSAGE_ACTIONS.ADD);
          messageRefs.current[secondResponseId]?.highlightAll();
        } else {
          // Add to existing merge if not already included
          if (!messages.includes(firstResponseId)) {
            updateMessages(firstResponseId, MESSAGE_ACTIONS.ADD);
            messageRefs.current[firstResponseId]?.highlightAll();
          }

          if (!messages.includes(secondResponseId)) {
            updateMessages(secondResponseId, MESSAGE_ACTIONS.ADD);
            messageRefs.current[secondResponseId]?.highlightAll();
          }

          setActiveTab(MERGE_STATES.HIGHLIGHT);
        }
      };

      // Listen for ring click signals from user messages
      useEffect(() => {
        if (
          ringClickSignal &&
          ringClickSignal.userMessageIndex === userMessageIndex
        ) {
          // This AI response component should handle the ring click
          handleRingClick(ringClickSignal.ringIndex);
        }
      }, [ringClickSignal, userMessageIndex]);

      // Listen for dot reorder signals from AI responses (for user messages only)
      useEffect(() => {
        if (
          dotReorderSignal &&
          dotReorderSignal.userMessageIndex === index &&
          isUser()
        ) {
          // Check if this is a reset signal (newOrder is null)
          if (dotReorderSignal.newOrder === null) {
            setCustomDotOrder(null); // Reset to original order
          } else if (
            dotReorderSignal.newDotOrder &&
            dotReorderSignal.newDotOrder.length > 0
          ) {
            setCustomDotOrder(dotReorderSignal.newDotOrder);
          }
        }
      }, [dotReorderSignal, index]);

      const getMergeCard = (items) => {
        const mergeItems = items.filter((i) => i.merged);

        if (mergeItems.length === 0) return;
        const ele = mergeItems[activeMerge];
        return getCardMergeContent(
          ele,
          mergeItems.length,
          'merge-' + activeMerge
        );
      };

      const getCardMergeContent = (ele, total, index) => {
        const getModelsMerge = () => {
          return Array.isArray(ele.model) ? ele.model : [ele.model];
        };

        const nextTab = () => {
          handleTab(activeMerge + 1);
          setPrevTab(activeMerge);
          setActiveMerge((prev) => prev + 1);
        };

        const previousTab = () => {
          handleTab(activeMerge - 1);
          setPrevTab(activeMerge);
          setActiveMerge((prev) => prev - 1);
        };

        const handleTab = (active) => {
          if (renderType === TABS) {
            const mergeItems = items.filter((i) => i.merged);
            const realEle = mergeItems[active];
            handleOutput(realEle, 'merge-' + active);
          }
        };

        const checkAnimation = () => {
          return (
            (activeMerge > prevTab && activeMerge != total - 1) ||
            activeMerge == 0
          );
        };

        const getTabsButtons = () => {
          return (
            total > 1 && (
              <div
                className="flex text-xs items-center text-battleship-gray"
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Previous" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      previousTab();
                    }}
                    disabled={activeMerge == 0}
                  >
                    <ChevronLeftIcon className="h-3 w-3 text-peachy-maroney" />
                  </IconButton>
                </Tooltip>
                <div>
                  {activeMerge + 1} / {total}
                </div>
                <Tooltip title="Next" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      nextTab();
                    }}
                    disabled={activeMerge == total - 1}
                  >
                    <ChevronRightIcon className="h-3 w-3 text-peachy-maroney" />
                  </IconButton>
                </Tooltip>
              </div>
            )
          );
        };

        const showHighlights = (index) => {
          setHighlightModel(ele.modelRef[index]);
          setHighlightMerge(ele._id);
        };

        const hideHighlights = () => {
          setHighlightModel(null);
          setHighlightMerge(null);
        };

        const getMergeTabs = () => {
          return (
            <>
              <AnimatePresence mode="wait">
                <motion.div
                  className={`flex-1 overflow-auto overflow-rtl ${
                    themeValue === 'dark'
                      ? 'overflow-rtl-dark'
                      : 'overflow-rtl-light'
                  }`}
                  style={{ overflowX: 'hidden' }}
                  key={activeMerge}
                  initial={{ opacity: 0, x: checkAnimation() ? 50 : -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: checkAnimation() ? -50 : 50 }}
                  transition={{ duration: 0.3 }}
                >
                  {ChatBubbleContent(
                    ele,
                    deleted,
                    index,
                    renderType !== TABS,
                    renderType === TABS && getTabsButtons()
                  )}
                </motion.div>
              </AnimatePresence>
              {expandedIdx.includes(index) && renderType !== TABS && (
                <div className="flex md:hidden justify-center">
                  {getButtons(ele, renderType !== TABS)}
                  <div>{getTabsButtons()}</div>
                </div>
              )}
            </>
          );
        };

        const mergeCardId = `merge-card-${ele._id}`;
        const mergeSourceId = `merge-source-${ele._id}`;

        return (
          <>
            {renderType === TABS ? (
              getMergeTabs()
            ) : (
              <div
                ref={(node) => {
                  if (registerCardRef) registerCardRef(mergeCardId, node);
                }}
                style={{
                  transform:
                    'translateX(4px)' /* Adjusted to shift 4px right */,
                }}
                className={`flex flex-col px-4 py-5 relative ${
                  themeValue === 'dark' ? 'bg-night-black' : 'bg-white'
                } rounded-[22px]
              ${
                isSelectedNoMerging(ele) && 'border-[3px] border-peachy-maroney'
              }
              overflow-auto overflow-x-hidden shadow-[4px_4px_7.6px_0px_rgba(0,0,0,0.10)] ${
                renderType === COLUMNS ? 'max-w-[70%]' : ''
              } ${shouldAlignMergeCardRight(items) ? '' : 'mx-auto'} w-fit`}
              >
                {/* Tabs */}
                <div className="relative">
                  {ele.data.role !== 'user' && (
                    <div
                      className="flex items-center justify-between cursor-pointer select-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        isSelected(ele) ? null : handleOutput(ele, index);
                      }}
                    >
                      <div className="flex gap-2 items-center mx-auto md:mx-0">
                        <Tooltip
                          title={
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {ele.merge_metadata && ele.merge_metadata.tool
                                ? ele.merge_metadata.operation.description
                                : ele.merge_metadata.rawPrompt}
                            </div>
                          }
                          placement="right"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                fontFamily: 'Figtree',
                                fontSize: '13px',
                                lineHeight: '13px',
                                fontWeight: 500,
                                bgcolor: '#FCE4D6',
                                color: '#51526C',
                                '& .MuiTooltip-arrow': {
                                  color: '#FCE4D6',
                                },
                                display: '-webkit-box',
                                WebkitLineClamp: 20,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              },
                            },
                          }}
                        >
                          <div className="flex items-center text-xs leading-5 font-bold uppercase text-peachy-maroney line-clamp-">
                            {/* Show SVG logo only for selected merge card and only on mobile */}
                            {isSelected(ele) && (
                              <span className="inline-block sm:hidden align-middle mr-1">
                                <TreeLogo
                                  style={{
                                    width: 16,
                                    height: 14,
                                    color: '#F7B267',
                                  }}
                                  className="text-peachy-maroney"
                                />
                              </span>
                            )}
                            {isMobile
                              ? getFirstWord(
                                  ele.merge_metadata.operation.name ?? 'Unify'
                                )
                              : ele.merge_metadata.operation.name ??
                                'Unify responses'}
                            :
                          </div>
                        </Tooltip>
                        <div className="flex flex-wrap gap-2">
                          {getModelsMerge().map((model, idx) => (
                            <div
                              onMouseEnter={() => showHighlights(idx)}
                              onMouseLeave={() => hideHighlights()}
                              className={`overflow-hidden w-fit capitalize rounded-md px-1 text-[12px] ${
                                isSelected(ele)
                                  ? 'bg-peachy-maroney text-white'
                                  : 'bg-platinum text-cool-gray dark:bg-tornado-cloud dark:text-powder-blue'
                              } font-bold flex whitespace-nowrap gap-1 items-center justify-start md:mx-0`}
                            >
                              <p>{model}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="hidden md:flex">
                        {getTabsButtons()}
                        {getButtons(ele, true)}
                      </div>
                      <div className="block md:hidden">
                        <IconButton onClick={() => handleExpanded(index)}>
                          <ChevronDownIcon
                            className={`h-4 w-4 text-battleship-gray duration-500 ${
                              expandedIdx.includes(index) && 'rotate-[180deg]'
                            }`}
                          />
                        </IconButton>
                      </div>
                    </div>
                  )}
                </div>
                {/* Content */}
                {getMergeTabs()}

                {/* Bottom Anchor Point (for outgoing connection) */}
                <div
                  ref={(node) =>
                    registerDotRef && registerDotRef(mergeSourceId, node)
                  } // Use registerDotRef as it just stores ID/node
                  className="absolute bottom-0 left-1/2 w-4 h-4" // Smaller width for more precise centering
                ></div>
              </div>
            )}
          </>
        );
      };

      const handleExpanded = (idx) => {
        setExpandedIdx((prevNumbers) => {
          if (prevNumbers.includes(idx)) {
            return prevNumbers.filter((n) => n !== idx);
          } else {
            return [...prevNumbers, idx];
          }
        });
      };

      const centerMessage = (item) => {
        return (
          renderType === TABS ||
          renderType === ROWS ||
          isUser() ||
          !Array.isArray(item) ||
          (Array.isArray(item) && item?.length === 1)
        );
      };

      const fullModels = (item) => {
        return (
          item &&
          Array.isArray(item) &&
          item.filter((i) => !i.merged).length === 4
        );
      };

      const checkErrorSelected = (item) => {
        const selection = Array.isArray(item)
          ? item.find((i) => !i.merged && i.selected)
          : item;
        return (
          !selection || (selection && selection.error && selection.error.type)
        );
      };

      const manyResponses = () => {
        return Array.isArray(item) && item.filter((i) => !i.merged).length > 1;
      };

      {
        /* Disable merge buttons - Ideogram & Runway */
      }
      const hasSpecificCapabilities = (item) => {
        const specificCapabilities = [
          '673e77c260fbf24164334533',
          '6748ded2b344fda8a0338cc5',
        ];
        if (Array.isArray(item)) {
          const result = item.some((ele) =>
            specificCapabilities.includes(ele?.data?.function?.capability)
          );
          return result;
        }
        const result = specificCapabilities.includes(
          item?.data?.function?.capability
        );
        return result;
      };

      const getLastMessageButtons = () => {
        return (
          <>
            {items.some((i) => i.merged) && renderType !== COLUMNS && (
              <Tooltip
                title={
                  collapsed
                    ? 'Expand original responses'
                    : 'Collapse original responses'
                }
                placement="right"
                arrow
              >
                <div
                  className="flex items-center justify-center w-[34px] h-[34px] bg-tranquil-peach hover:bg-tranquil-peach/70 cursor-pointer select-none rounded-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCollapsed((prev) => !prev);
                  }}
                >
                  <ChevronUpIcon
                    className={`h-5 w-5 text-peachy-maroney duration-500 ${
                      collapsed && 'rotate-[180deg]'
                    }`}
                  />
                </div>
              </Tooltip>
            )}

            {!mergeActive &&
              manyResponses() &&
              !hasSpecificCapabilities(item) && (
                <Tooltip title="Smart Merge panel" placement="right" arrow>
                  <div
                    className="flex items-center justify-center w-[34px] h-[34px] bg-peachy-maroney hover:bg-peachy-maroney/70 cursor-pointer select-none rounded-xl"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActive(true);
                      setMergeItem(item);
                    }}
                  >
                    <SmartMergeIcon className="h-7 w-7 text-white" />
                  </div>
                </Tooltip>
              )}

            {index === 0 && (
              <>
                <Tooltip title="Regenerate response" placement="right" arrow>
                  <div
                    className="flex items-center justify-center w-[34px] h-[34px] bg-nue-blue hover:bg-nue-blue/70 cursor-pointer select-none rounded-xl"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRegenerate();
                    }}
                  >
                    <ArrowPathIcon className="h-5 w-5 text-white" />
                  </div>
                </Tooltip>

                <Tooltip title="Continue response" placement="right" arrow>
                  <div
                    className={`flex items-center justify-center w-[34px] h-[34px] select-none rounded-xl ${
                      checkErrorSelected(item)
                        ? 'bg-platinum dark:bg-tornado-cloud'
                        : 'bg-nue-blue hover:bg-nue-blue/70 cursor-pointer'
                    }`}
                    onClick={(e) => {
                      if (checkErrorSelected(item)) {
                        return;
                      }
                      e.stopPropagation();
                      continueResponse();
                    }}
                  >
                    <ArrowRightIcon
                      className={`h-5 w-5 text-white ${
                        checkErrorSelected(item)
                          ? 'text-cool-gray dark:text-palladium'
                          : 'text-white'
                      }`}
                    />
                  </div>
                </Tooltip>

                <Tooltip title="Add response" placement="right" arrow>
                  <div
                    className={`flex items-center justify-center w-[34px] h-[34px] rounded-xl select-none ${
                      fullModels(item)
                        ? 'bg-platinum dark:bg-tornado-cloud'
                        : 'bg-nue-blue hover:bg-nue-blue/70 cursor-pointer'
                    }`}
                    onClick={() => (fullModels(item) ? null : onAddModels())}
                  >
                    <PlusIcon
                      className={`h-5 w-5 ${
                        fullModels(item)
                          ? 'text-cool-gray dark:text-palladium'
                          : 'text-white'
                      }`}
                    />
                  </div>
                </Tooltip>
              </>
            )}
          </>
        );
      };

      const getMergeButtons = () => {
        return (
          <>
            <Tooltip
              title={
                isMinimized
                  ? 'Expand Smart Merge panel'
                  : 'Collapse Smart Merge panel'
              }
              placement="right"
              arrow
            >
              <div
                className="flex items-center justify-center w-[34px] h-[34px] bg-peachy-maroney hover:bg-peachy-maroney/70 cursor-pointer select-none rounded-xl"
                onClick={(e) => {
                  e.stopPropagation();
                  setMinimized(!isMinimized);
                }}
              >
                <SmartMergeIcon className="h-7 w-7 text-white" />
              </div>
            </Tooltip>

            <Tooltip title="Exit Smart Merge" placement="right" arrow>
              <div
                className="flex items-center justify-center w-[34px] h-[34px] bg-lavender hover:bg-lavender/70 cursor-pointer select-none rounded-xl"
                onClick={(e) => {
                  e.stopPropagation();
                  setActive(false);
                }}
              >
                <ArrowRightStartOnRectangleIcon className="h-5 w-5 text-violet-blue" />
              </div>
            </Tooltip>
          </>
        );
      };

      const handleMouseDown = (e) => {
        if (e.button !== 0) return;

        const startPosition = { x: e.clientX, y: e.clientY };
        setInitialPosition(startPosition);
        setMousePosition(startPosition);

        const timer = setTimeout(() => {
          const distance = Math.sqrt(
            Math.pow(mousePosition.x - startPosition.x, 2) +
              Math.pow(mousePosition.y - startPosition.y, 2)
          );

          if (distance <= MOVEMENT_THRESHOLD) {
            setIsDragging(true);
            e.target.style.cursor = 'move';
          }
        }, 300);

        setLongPressTimeout(timer);
      };

      const handleMouseMove = (e) => {
        const currentPosition = { x: e.clientX, y: e.clientY };
        setMousePosition(currentPosition);

        if (longPressTimeout && initialPosition) {
          const distance = Math.sqrt(
            Math.pow(currentPosition.x - initialPosition.x, 2) +
              Math.pow(currentPosition.y - initialPosition.y, 2)
          );

          if (distance > MOVEMENT_THRESHOLD && !isDragging) {
            clearTimeout(longPressTimeout);
            setLongPressTimeout(null);
            e.target.style.cursor = 'text';
          }
        }
      };

      const handleMouseUp = (e) => {
        if (longPressTimeout) {
          clearTimeout(longPressTimeout);
          setLongPressTimeout(null);
        }

        setIsDragging(false);
        setInitialPosition(null);
        e.target.style.cursor = 'text';

        changeSensorsState(false);
      };

      const handleDragStart = () => {
        if (isDragging) {
          changeSensorsState(true);
        }
      };

      const handleDragEnd = (e) => {
        setIsDragging(false);
        setInitialPosition(null);
        e.target.style.cursor = 'text';

        changeSensorsState(false);
      };

      const changeSensorsState = (state) => {
        if (sensors) {
          sensors.forEach((sensor) => (sensor.enabled = state));
        }
      };

      return (
        <>
          {isDownloadDialogOpen && messageToDownload && (
            <DownloadOptionsDialog
              open={isDownloadDialogOpen}
              onClose={() => {
                setIsDownloadDialogOpen(false);
                setMessageToDownload(null);
              }}
              message={messageToDownload.data}
              api={store?.api}
              availableTypes={availableDownloadTypes}
            />
          )}
          {selectionChangeWarning.open && (
            <SelectionChangeWarning
              open={selectionChangeWarning.open}
              onClose={handleCloseSelectionWarning}
              onConfirm={handleConfirmSelectionWarning}
            />
          )}
          {conversationPathWarning.open && (
            <ConversationPathWarning
              open={conversationPathWarning.open}
              onClose={handleCloseConversationPathWarning}
              onConfirm={handleConfirmConversationPathWarning}
            />
          )}
          {!isHidden(item) && (
            <div
              className={`flex gap-[4px] w-full ${
                centerMessage(item) && 'lg:max-w-4xl m-auto'
              }`}
            >
              <div className="hidden md:block">
                {/* AI Robot */}
                {!isUser() && (
                  <div className="h-min relative group flex flex-col items-center group">
                    {/* <div className={renderType === TABS && 'avatar'}> */}
                    <div className="w-12 mt-2">
                      {renderType === TABS ? (
                        <div className="w-full h-full flex items-center justify-center">
                          {getModelIcon()}
                        </div>
                      ) : (
                        <>
                          {!loading && !mergeActive && (
                            <div className="py-2 flex flex-col gap-4 w-min m-auto">
                              {getLastMessageButtons()}
                            </div>
                          )}
                          {mergeActive && item == mergeItem && !isMobile && (
                            <div className="py-2 flex flex-col gap-4 w-min m-auto">
                              {getMergeButtons()}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {/* </div> */}
                  </div>
                )}
                {/* Person - avatar */}
                {isUser() && (
                  <div className="h-min relative group flex flex-col items-center group">
                    {avatar ? (
                      <div className="avatar">
                        <div className="w-12">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: avatar,
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <UserCircleIcon
                        className={`w-12 h-12 ${
                          themeValue === 'dark'
                            ? 'text-tornado-cloud'
                            : 'text-cool-gray'
                        }`}
                      />
                    )}
                  </div>
                )}
              </div>
              <div
                key={index}
                className={`w-full relative pb-2 lg:pb-2 pr-4 pl-4 md:pl-0 pt-2 text-md
                  font-figtree text-base  font-normal ${
                    themeValue === 'dark'
                      ? 'text-crystal-bell'
                      : 'text-raisin-black'
                  }`}
              >
                <CssBaseline />
                {getContentMessage()}
                {index === 0 && renderType !== TABS && (
                  <div className="relative w-full md:hidden">
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex gap-4 mx-auto justify-center">
                      {!loading ? (
                        getLastMessageButtons()
                      ) : (
                        <div className="h-[34px]"></div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      );
    }
  )
);

export default memo(ChatMessage);
